const BASE_CHAIN_ID_HEX = '0x2105';
const BASE_CHAIN_ID_DECIMAL = '8453';
const BASE_USDC_TOKEN_ADDRESS = '0x833589fCD6EDB6E08f4c7C32D4f71b54bdA02913';

function getWindowEthereum() {
  if (typeof window === 'undefined') return null;
  return window.ethereum || null;
}

function getInjectedProviders() {
  const ethereum = getWindowEthereum();
  if (!ethereum) return [];

  if (Array.isArray(ethereum.providers) && ethereum.providers.length > 0) {
    return ethereum.providers.filter((provider) => Boolean(provider?.request));
  }

  return ethereum?.request ? [ethereum] : [];
}

function getProviderIdentity(provider) {
  if (!provider) {
    return {
      walletType: 'injected',
      walletLabel: 'Injected wallet',
      providerFlags: [],
    };
  }

  const providerFlags = [
    provider.isMetaMask ? 'metamask' : null,
    provider.isCoinbaseWallet ? 'coinbase' : null,
    provider.isRabby ? 'rabby' : null,
    provider.isFrame ? 'frame' : null,
    provider.isTrust || provider.isTrustWallet ? 'trustwallet' : null,
    provider.isBraveWallet ? 'brave' : null,
  ].filter(Boolean);

  if (provider.isMetaMask) {
    return {
      walletType: 'metamask',
      walletLabel: 'MetaMask',
      providerFlags,
    };
  }

  if (provider.isCoinbaseWallet) {
    return {
      walletType: 'coinbase',
      walletLabel: 'Coinbase Wallet',
      providerFlags,
    };
  }

  if (provider.isRabby) {
    return {
      walletType: 'rabby',
      walletLabel: 'Rabby',
      providerFlags,
    };
  }

  if (provider.isFrame) {
    return {
      walletType: 'frame',
      walletLabel: 'Frame',
      providerFlags,
    };
  }

  if (provider.isTrust || provider.isTrustWallet) {
    return {
      walletType: 'trustwallet',
      walletLabel: 'Trust Wallet',
      providerFlags,
    };
  }

  if (provider.isBraveWallet) {
    return {
      walletType: 'brave',
      walletLabel: 'Brave Wallet',
      providerFlags,
    };
  }

  return {
    walletType: 'injected',
    walletLabel: 'Injected wallet',
    providerFlags,
  };
}

function selectInjectedProvider() {
  const providers = getInjectedProviders();
  if (providers.length === 0) return null;

  const priorityMatchers = [
    (provider) => provider?.isMetaMask,
    (provider) => provider?.isCoinbaseWallet,
    (provider) => provider?.isRabby,
    (provider) => provider?.isFrame,
    (provider) => provider?.isTrust || provider?.isTrustWallet,
    (provider) => provider?.isBraveWallet,
  ];

  for (const matcher of priorityMatchers) {
    const match = providers.find(matcher);
    if (match) return match;
  }

  return providers[0];
}

export function getInjectedWalletEnvironment() {
  const providers = getInjectedProviders();
  const selectedProvider = selectInjectedProvider();
  const selectedIdentity = getProviderIdentity(selectedProvider);

  return {
    available: providers.length > 0,
    providerCount: providers.length,
    selectedWalletType: selectedIdentity.walletType,
    selectedWalletLabel: selectedIdentity.walletLabel,
    detectedWalletLabels: providers.map((provider) => getProviderIdentity(provider).walletLabel),
  };
}

function getEthereumProvider() {
  return selectInjectedProvider();
}

function getNoProviderErrorMessage() {
  const environment = getInjectedWalletEnvironment();
  if (environment.providerCount > 1) {
    return `No compatible injected wallet is currently available for this action. Detected providers: ${environment.detectedWalletLabels.join(', ')}.`;
  }
  return 'No injected wallet was found. Install MetaMask, Coinbase Wallet, Rabby, or another compatible browser wallet.';
}

export function hasInjectedWalletProvider() {
  return getInjectedProviders().length > 0;
}

export function isBaseChainId(chainId) {
  if (typeof chainId !== 'string') return false;
  const normalized = chainId.trim().toLowerCase();
  return normalized === BASE_CHAIN_ID_HEX || normalized === BASE_CHAIN_ID_DECIMAL;
}

export async function connectInjectedWallet() {
  const provider = getEthereumProvider();
  if (!provider?.request) {
    throw new Error(getNoProviderErrorMessage());
  }

  const accounts = await provider.request({ method: 'eth_requestAccounts' });
  const chainId = await provider.request({ method: 'eth_chainId' }).catch(() => null);
  const selectedAddress = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;

  if (!selectedAddress) {
    throw new Error('Wallet connected, but no account was returned.');
  }

  const providerIdentity = getProviderIdentity(provider);

  return {
    address: selectedAddress,
    chainId,
    walletType: providerIdentity.walletType,
    walletLabel: providerIdentity.walletLabel,
    providerFlags: providerIdentity.providerFlags,
    providerCount: getInjectedProviders().length,
    detectedWalletLabels: getInjectedProviders().map((candidate) => getProviderIdentity(candidate).walletLabel),
  };
}

export async function getConnectedInjectedWallet() {
  const provider = getEthereumProvider();
  if (!provider?.request) {
    return null;
  }

  const accounts = await provider.request({ method: 'eth_accounts' }).catch(() => []);
  const chainId = await provider.request({ method: 'eth_chainId' }).catch(() => null);
  const selectedAddress = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;

  if (!selectedAddress) {
    return null;
  }

  const providerIdentity = getProviderIdentity(provider);

  return {
    address: selectedAddress,
    chainId,
    walletType: providerIdentity.walletType,
    walletLabel: providerIdentity.walletLabel,
    providerFlags: providerIdentity.providerFlags,
    providerCount: getInjectedProviders().length,
    detectedWalletLabels: getInjectedProviders().map((candidate) => getProviderIdentity(candidate).walletLabel),
  };
}

export async function switchInjectedWalletToBase() {
  const provider = getEthereumProvider();
  if (!provider?.request) {
    throw new Error(getNoProviderErrorMessage());
  }

  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BASE_CHAIN_ID_HEX }],
    });
  } catch (error) {
    if (error?.code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: BASE_CHAIN_ID_HEX,
          chainName: 'Base',
          nativeCurrency: {
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18,
          },
          rpcUrls: ['https://mainnet.base.org'],
          blockExplorerUrls: ['https://basescan.org'],
        }],
      });
    } else {
      throw error;
    }
  }

  return getConnectedInjectedWallet();
}

function stripHexPrefix(value) {
  return String(value || '').replace(/^0x/i, '');
}

function toPaddedHex(value, length = 64) {
  return String(value).padStart(length, '0');
}

function decimalAmountToBaseUnits(amount, decimals = 6) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    throw new Error('Invalid token amount.');
  }

  const fixed = numeric.toFixed(decimals);
  const [whole, fraction = ''] = fixed.split('.');
  const normalized = `${whole}${fraction.padEnd(decimals, '0')}`.replace(/^0+/, '') || '0';
  return BigInt(normalized);
}

function encodeErc20Transfer({ to, amount }) {
  const normalizedTo = stripHexPrefix(to);
  if (normalizedTo.length !== 40) {
    throw new Error('Destination wallet address is invalid.');
  }

  const amountHex = amount.toString(16);
  return `0xa9059cbb${toPaddedHex(normalizedTo)}${toPaddedHex(amountHex)}`;
}

export async function sendBaseUsdcTransfer({ to, amount, from }) {
  const provider = getEthereumProvider();
  if (!provider?.request) {
    throw new Error(getNoProviderErrorMessage());
  }

  const wallet = await getConnectedInjectedWallet();
  if (!wallet?.address) {
    throw new Error('Connect your wallet before sending a contribution.');
  }

  if (!isBaseChainId(wallet.chainId)) {
    throw new Error('Wallet must be connected to Base before sending a USDC contribution.');
  }

  const sender = from || wallet.address;
  const baseUnits = decimalAmountToBaseUnits(amount, 6);
  const data = encodeErc20Transfer({ to, amount: baseUnits });

  const txHash = await provider.request({
    method: 'eth_sendTransaction',
    params: [{
      from: sender,
      to: BASE_USDC_TOKEN_ADDRESS,
      value: '0x0',
      data,
    }],
  });

  return {
    txHash,
    from: sender,
    to,
    tokenAddress: BASE_USDC_TOKEN_ADDRESS,
    amount,
  };
}
